import React from 'react';
import theme from './ProfileMemberPermissionsList.module.scss';
import { ConfirmButton, Button } from '@/components/Buttons';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import PermissionModeIcon from '@/components/permissions/PermissionModeIcon';
import { ProfileMemberDetails, MemberStatus } from '@/types';
import { SimpleCard, CardSection } from '@/components/Cards';
import { resendProfileInvite } from '@/services/profiles';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { useLingui } from '@lingui/react';

interface CommonProps {
    onCancelInviteClick(memberDetails: ProfileMemberDetails): void;
    onEditPermissionsClick(memberDetails: ProfileMemberDetails): void;
}

type ProjectMemberPermissionsListProps = CommonProps & {
    members: ProfileMemberDetails[];
};

const ProfileMemberPermissionsList: React.FC<ProjectMemberPermissionsListProps> = ({
    members,
    onCancelInviteClick,
    onEditPermissionsClick,
}) => {
    return (
        <div>
            {members.map((member) => (
                <ProfileMemberPermissionsListItem
                    key={member.profileMemberId + ''}
                    member={member}
                    onCancelInviteClick={onCancelInviteClick}
                    onEditPermissionsClick={onEditPermissionsClick}
                />
            ))}
        </div>
    );
};

type ProjectMemberPermissionsListItemProps = CommonProps & {
    member: ProfileMemberDetails;
};

const ProfileMemberPermissionsListItem: React.FC<ProjectMemberPermissionsListItemProps> = ({
    member,
    onCancelInviteClick,
    onEditPermissionsClick,
}) => {
    const { _ } = useLingui();
    const resendInvite = () => {
        resendProfileInvite(member.inviteId!);
        Toast.success(<Trans>Invitation resent</Trans>);
    };
    function renderButtons() {
        return (
            <div className="row-nowrap">
                {member.status === MemberStatus.Invited && (
                    <ConfirmButton
                        color="tertiary"
                        onConfirm={() => triggerHandler(onCancelInviteClick, member)}
                        dialogTitle={<Trans>Cancel invitation</Trans>}
                        dialogBody={<Trans>Are you sure you want to cancel the invitation to {member.email}?</Trans>}>
                        <Trans>Cancel invitation</Trans>
                    </ConfirmButton>
                )}

                <Button color="tertiary" onClick={(e) => triggerHandler(onEditPermissionsClick, member)}>
                    <Trans>Edit Permissions</Trans>
                </Button>
            </div>
        );
    }

    return (
        <SimpleCard>
            <CardSection width="30%" caption={<Trans>User</Trans>}>
                {member.displayName && member.displayName !== member.email && <div>{member.displayName}</div>}
                <div>{member.email}</div>
                {member.status === MemberStatus.Invited && (
                    <ConfirmButton
                        dialogBody={<Trans>Are you sure you want to resend invitation to {member.email}?</Trans>}
                        dialogTitle={<Trans>Resend invitation</Trans>}
                        onConfirm={resendInvite}
                        className={theme.resendInviteButton}
                        color="link">
                        <Trans>Resend invitation</Trans>
                    </ConfirmButton>
                )}
            </CardSection>

            <CardSection width="10%" caption={<Trans>Status</Trans>}>
                {_({ id: `MemberStatus.${member.status}` })}
            </CardSection>

            {/* <RowSection width="200px" title={<Trans>Roles</Trans>}>
                    <ul className={style.permissionDetailList}>
                        {permissionSet.selectedRoles.map(role =>
                            <li key={role.roleId}>{role.roleKey}</li>
                        )}

                        {permissionSet.selectedRoles.length === 0 && <li className="text-missing"><Trans>None</Trans></li>}
                    </ul>
                </RowSection> */}

            <CardSection width="20%" caption={<Trans>Permissions</Trans>}>
                <ul className="simple-list">
                    {member.isAdministrator ? (
                        <>
                            <PermissionModeIcon value="Allow" />
                            &nbsp;
                            <Trans>Administrator</Trans>
                        </>
                    ) : (
                        member.profilePermissions.map((permission) => (
                            <li key={permission}>
                                <PermissionModeIcon value="Allow" />
                                &nbsp;
                                {_({ id: `ProfilePermissionType.${permission}` })}
                            </li>
                        ))
                    )}

                    {member.profilePermissions.length === 0 && !member.isAdministrator && (
                        <li className="text-missing">
                            <Trans>None</Trans>
                        </li>
                    )}
                </ul>
            </CardSection>
            {/* TODO: Fix me */}
            <CardSection width="40%">{renderButtons()}</CardSection>
        </SimpleCard>
    );
};

export default ProfileMemberPermissionsList;
